import { find } from 'lodash';

import {
  ChipState,
  PERSIST_KEYS,
  StoreKeys,
} from '../../store/useChipStore/chipStore';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';

export { showMemberSelector } from './memberSelectorUtils';
export { getFeatureFlag, getFeatureFlags } from './getFeatureFlag';

export const isDetailsPage = (page) =>
  page === ConstantsRoutes.FACILITY_DETAILS.key ||
  page === ConstantsRoutes.PROVIDER_DETAILS.key ||
  page === ConstantsRoutes.PROVIDER_GROUP_DETAILS.key;

export const interleave = (originalArray, thingToPutBetweenArray) =>
  []
    .concat(...originalArray.map((n) => [n, thingToPutBetweenArray]))
    .slice(0, -1);

export const hasValue = (value) => value !== undefined && value !== null;

export const getOnlyDefinedValuesFromObject = (obj) => {
  const definedValues = {};
  Object.keys(obj).forEach((key) => {
    if (hasValue(obj[key])) {
      definedValues[key] = obj[key];
    }
  });
  return definedValues;
};

export const convertObjectToUrlParams = (chipStore: ChipState, paramsObj) => {
  const enableRemoveParamsInUrl =
    sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.ENABLE_REMOVE_SEARCH_PARAMS_IN_URL
    ) === 'true';
  if (enableRemoveParamsInUrl) {
    // Analyse and include if any params are required to be added in the URL
    return '';
  }

  const persistValues = chipStore
    ? Object.fromEntries(
        Object.entries(chipStore).filter(([k]) =>
          PERSIST_KEYS.includes(<StoreKeys>k)
        )
      )
    : {};

  // Adding includeSpecialityRollupCodes increases the size of the URL parameters and the http header, so it is being removed.
  // includeSpecialityRollupCodes values are avalilable in typeaheadStoreValues.
  const restParamsObj = { ...paramsObj };
  if ('includeSpecialityRollupCodes' in restParamsObj) {
    delete restParamsObj.includeSpecialityRollupCodes;
  }

  if ('combinedIncludeSpecialityRollupCodes' in restParamsObj) {
    delete restParamsObj.combinedIncludeSpecialityRollupCodes;
  }

  if ('pesKeyword' in restParamsObj) {
    delete restParamsObj.pesKeyword;
  }

  if ('typeaheadLinkName' in restParamsObj) {
    delete restParamsObj.typeaheadLinkName;
  }

  if ('vertical' in restParamsObj) {
    delete restParamsObj.vertical;
  }

  if ('specialtyCode' in restParamsObj) {
    delete restParamsObj.specialtyCode;
  }

  if ('includeOrgTypeCodes' in restParamsObj) {
    delete restParamsObj.includeOrgTypeCodes;
  }

  if ('excludeOrgTypeCodes' in restParamsObj) {
    delete restParamsObj.excludeOrgTypeCodes;
  }

  const mergedVals = {
    ...persistValues,
    ...restParamsObj,
  };

  const urlParams: string[] = [];
  Object.keys(mergedVals).forEach((key) => {
    if (hasValue(mergedVals[key])) {
      urlParams.push(`${key}=${encodeURIComponent(mergedVals[key])}`);
    }
  });
  return `?${urlParams.join('&')}`;
};

export const convertUrlParamsToObject = (urlParams) => {
  const paramsObj = {};

  for (const [key, value] of urlParams) {
    if (hasValue(value)) {
      paramsObj[key] =
        value === 'true' || value === 'false' ? value === 'true' : value;
    }
  }
  return paramsObj;
};

// This function checks a condition and returns either val1 or val2 based on the condition.
export const valCheck = (condition: any, val1: any, val2: any) =>
  condition ? val1 : val2;

export const getEligibilityByChipValue = (
  eligibilities,
  chipValue,
  virtualCareSectionLandingPage
) => {
  if (!Array.isArray(eligibilities) || !chipValue) return [];
  if (!virtualCareSectionLandingPage?.includes(chipValue)) {
    return [];
  }
  switch (chipValue) {
    case Constants.CHIPS_CATEGORIES.PRIMARY_CARE:
      return eligibilities?.filter(
        (eligibility) =>
          eligibility?.careGroup === 'mainCare' &&
          eligibility?.serviceType === 'primaryCare'
      );
    case Constants.CHIPS_CATEGORIES.MEDICAL_SPECIALISTS:
      return eligibilities?.filter(
        (eligibility) => eligibility?.careGroup === 'specialtyCare'
      );
    case Constants.CHIPS_CATEGORIES.FACILITIES:
      return eligibilities?.filter(
        (eligibility) =>
          eligibility?.careGroup === 'mainCare' &&
          eligibility?.serviceType === 'urgentCare'
      );
    default:
      return [];
  }
};

export const getProviderTitle = (serviceType, uiMessaging, provider) => {
  if (serviceType === 'primaryCare') {
    const titleContent = find(uiMessaging, {
      key: ConstantsLagoon.VIRTUAL_CARE.VIRTUAL_PRIMARY_CARE_LANDING_PAGE,
    });
    return titleContent?.title;
  } else if (serviceType === 'urgentCare') {
    const titleContent = find(uiMessaging, {
      key: ConstantsLagoon.VIRTUAL_CARE.VIRTUAL_URGENT_CARE_LANDING_PAGE,
    });
    return titleContent?.title;
  } else {
    const titleContent = uiMessaging?.find(
      (row) =>
        row.key ===
        `VIRTUAL_${provider?.providerName?.toUpperCase().replace(/\s+/g, '_')}`
    );
    return titleContent?.title;
  }
};
